<template>
  <div class="mt-6 leading-normal">
    <panel class="pt-6 mb-6">
      <div class="flex flex-wrap -mx-2">
        <div class="w-full md:w-1/4 px-2">
          <label for="search_term" class="text-sm">Search</label>
          <input type="text" id="search_term" v-model="searchParams.q" class="block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
        </div>

        <div class="w-full md:w-1/4 px-2">
          <label for="search_term" class="text-sm">Completed After</label>
          <date-time-picker v-model="searchParams.completed_after" format="d LLLL yyyy" clear-button value-format="yyyy-LL-dd" value-type="String" auto-close constraints-format="yyyy-LL-dd" :class="'block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm mt-2'" :max-date="searchParams.completed_before" :time-picker="false" />
        </div>

        <div class="w-full md:w-1/4 px-2">
          <label for="search_term" class="text-sm">Completed Before</label>
          <date-time-picker v-model="searchParams.completed_before" format="d LLLL yyyy" clear-button value-format="yyyy-LL-dd" value-type="String" auto-close constraints-format="yyyy-LL-dd" :class="'block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 h-10 text-sm mt-2'" :min-date="searchParams.completed_after" :time-picker="false" />
        </div>
      </div>
    </panel>

    <loader v-if="results === null" />
    <div v-else-if="results.data.length === 0" class="pt-6 flex flex-col text-center">
      <i class="fas fa-info-circle mb-3"></i> Your search returned no results.
    </div>
    <panel v-else class="pt-6">
      <div class="w-full max-w-full overflow-x-auto overflow-y-hidden">
        <table class="table-auto text-center w-full">
          <thead class="uppercase text-xs">
            <tr>
              <th class="text-left p-2 whitespace-no-wrap">Property</th>
              <th class="text-center p-2 whitespace-no-wrap">Status</th>
              <th class="text-center p-2 whitespace-no-wrap">Completion</th>
              <th class="text-center p-2 whitespace-no-wrap">Sold Price</th>
              <th class="text-left p-2 whitespace-no-wrap">Fees</th>
              <th class="text-center p-2">Head Office Fee</th>
              <th class="text-center p-2" v-if="[7, 8].includes(selectedOffice.network_id)">Invoice Date</th>
              <th class="text-center p-2" v-if="![7, 8].includes(selectedOffice.network_id)">Month-end Invoice Report</th>
              <th class="text-right p-2"></th>
            </tr>
          </thead>
          <tbody class="text-sm">
            <tr v-for="property in results.data" :key="property.id" class="border-t border-grey-lighter">
              <td class="p-2 text-left">
                <strong>{{ property.address }}</strong>
              </td>
              <td class="p-2 text-center">
                {{ completionStatus(property.status_id) }}
                <small class="block text-grey-dark mt-1 whitespace-no-wrap">
                  Logged: {{ property.updated_at | datetime }}
                </small>
                <small class="block text-grey-dark mt-1 whitespace-no-wrap" v-if="property.withdrawn_status_id">
                  {{ withdrawnStatus(property.withdrawn_status_id) }} <span v-if="property.selling_agent"> - {{ property.selling_agent }}</span>
                </small>
              </td>
              <td class="p-2 text-center">
                <span v-if="[0, 3].includes(property.status_id)">N/A</span>
                <span v-else>
                  {{ property.completed_at | date }}
                </span>
              </td>
              <td class="p-2 text-center">
                {{ property.completion_price | gbp }}
              </td>
              <td class="p-2 text-left whitespace-no-wrap">
                <span v-if="[0, 3].includes(property.status_id)">N/A</span>
                <div v-else-if="property.network_fee.fee_basis_id === 1">
                  <div><strong>Base Fee:</strong> {{ property.office_fee.sales_completion_fee | gbp }}</div>
                  <div v-if="property.is_discounted">
                    <i class="fas fa-check-circle text-green-dark"></i> Low Value Discount: -{{ property.office_fee.low_value_discount | gbp }}
                  </div>
                  <div v-if="property.is_out_of_area">
                    <i class="fas fa-check-circle text-green-dark"></i> Out of Area Penalty: +{{ property.office_fee.out_of_area_penalty_fee | gbp }}
                  </div>
                  <div v-if="property.is_premium_listing">
                    <i class="fas fa-check-circle text-green-dark"></i> Premium Listing Fee: +{{ property.office_fee.premium_listing_fee | gbp }}
                  </div>
                </div>
                <div v-else-if="property.network_fee.fee_basis_id === 2">
                  <div><strong>Gross Commission: </strong> {{ property.gross_commission | gbp }} <span v-if="property.gross_commission_percent">({{ property.gross_commission_percent  }}%)</span></div>
                  <div v-if="property.has_third_party_commission"><strong>Net Commission:</strong> {{ property.net_commission | gbp }} <span v-if="property.net_commission_percent">({{ property.net_commission_percent  }}%)</span></div>
                </div>
              </td>
              <td class="p-2 text-center">
                <span v-if="[0, 3].includes(property.status_id)">N/A</span>
                <span v-else>
                  {{ property.total_fee | gbp }}
                  <span v-if="property.network_fee.fee_basis_id === 2">
                    ({{ property.office_fee.commission_share | number }}%)
                  </span>
                </span>
              </td>
              <td class="p-2" v-if="[7, 8].includes(selectedOffice.network_id)">
                <span v-if="[0, 3].includes(property.status_id)">N/A</span>
                <span v-else>{{ property.invoice_due_date | date }}</span>
              </td>
              <td class="p-2" v-if="![7, 8].includes(selectedOffice.network_id)">
                <a v-if="property.report" :href="property.report.report_url" class="text-primary">Download Report</a>
                <span v-else>Pending</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <paginator :data="results" @change="pageChanged"/>
    </panel>
  </div>
</template>

<script>
import { v2Api } from '@/http'
import { mapState } from 'vuex'
import debounce from 'lodash/debounce'
import DateTimePicker from 'vue-vanilla-datetime-picker'

export default {
  components: {
    DateTimePicker
  },
  data () {
    return {
      searchParams: {
        q: null,
        completed_after: null,
        completed_before: null,
        page: 1
      },
      pageHasChanged: false,
      results: null
    }
  },
  computed: {
    ...mapState(['selectedOffice'])
  },
  created () {
    this.search()
  },
  methods: {
    search: debounce(function () {
      if (this.pageHasChanged) {
        this.pageHasChanged = false
      } else {
        this.searchParams.page = 1
      }

      v2Api.get('completion-report-properties', {
        params: {
          ...this.searchParams,
          office_id: this.selectedOffice.id
        }
      }).then((response) => {
        this.results = response.data
      })
    }, 500),
    pageChanged (i) {
      this.searchParams.page = i
      this.pageHasChanged = true
    },
    completionStatus (id) {
      switch (id) {
        case 0: return 'Withdrawn'
        case 1: return 'Exchanged'
        case 2: return 'Completed'
        case 3: return 'Duplicate Property Listing'
      }
    },
    withdrawnStatus (id) {
      switch (id) {
        case 1: return 'Sold through other agent'
        case 2: return 'Vendor has decided not to sell - permanently removed from Market'
        case 3: return 'Instructed another agent'
        case 4: return 'Vendor has decided not to sell but will relaunch within 6 months with you'
        case 5: return 'Vendor has decided not to sell and will relaunch with another agent'
        case 6: return 'Other'
      }
    }
  },
  watch: {
    searchParams: {
      handler () {
        this.search()
      },
      deep: true
    },
    selectedOffice () {
      this.search()
    }
  }
}
</script>
